import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouterState,
} from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { lazy, Suspense, type ReactNode } from 'react'
import { ConfirmProvider } from '@scc/ui'
import { SessionLoader } from '@/modules/shared/components/session-loader'
import { useSessionBootstrap } from '@/lib/api/use-session-bootstrap'
import { useSessionStore } from '@/lib/session.store'
import '../styles/globals.css'

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((module) => ({
        default: module.ReactQueryDevtools,
      })),
    )
  : null

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: 'SCC Command Center' },
    ],
    links: [
      // Social Commerce Club brand favicon (pulled from socialcommerceclub.com).
      { rel: 'icon', type: 'image/png', href: '/favicon-32.png' },
      { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
    ],
  }),
})

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body suppressHydrationWarning>
        {children}
        <Scripts />
      </body>
    </html>
  )
}

const PUBLIC_PATH_PREFIXES = ['/login', '/register', '/portal', '/l', '/p']

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext()
  useSessionBootstrap()

  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const sessionStatus = useSessionStore((s) => s.sessionStatus)
  const token = useSessionStore((s) => s.token)

  // Block render on guarded paths until the bootstrap finishes. `idle` covers
  // the brief window before useSessionBootstrap has flipped status to `loading`.
  const validating =
    sessionStatus === 'loading' || (sessionStatus === 'idle' && !!token)
  const showLoader = validating && !isPublicPath(pathname)

  return (
    <RootDocument>
      <QueryClientProvider client={queryClient}>
        <ConfirmProvider>
          {showLoader ? <SessionLoader /> : <Outlet />}
        </ConfirmProvider>
        <Toaster
          theme="light"
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#FBF8F2',
              border: '1px solid #E5DDC9',
              color: '#1A1410',
              fontFamily: "'Inter', system-ui, sans-serif",
            },
          }}
        />
        {ReactQueryDevtools ? (
          <Suspense fallback={null}>
            <ReactQueryDevtools buttonPosition="bottom-left" />
          </Suspense>
        ) : null}
      </QueryClientProvider>
    </RootDocument>
  )
}
